import { useState, useEffect, useContext, useRef } from "react";
import ReactDOM from "react-dom";
import { ADModalBody, ADModalFooter, ADModalInfo, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton } from "./GlassButtons";
import { MapitLayerFactory } from "src/components/MapitLayerFactory";
import { LayerType, LayerInfo, LayerVariabilityType } from "src/common/managers/Types";
import { RememberImportsManager } from "src/managers/RememberImportsManager";
import { MdPalette, MdMessage, MdImage } from "react-icons/md";
import { FaMapMarkerAlt } from "react-icons/fa";
import { PiCaretRightLight } from "react-icons/pi";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { EmbeddedVectorMapEnhanced } from "src/compentsModus/EmbeddedVectorMapEnhanced";
import { SheetFunc } from "src/managers/SheetFunc";
import { MitDataType } from "src/common/managers/Types";
import { MapitStateContext } from "src/states/MapitState";
import { MitLayerHandle, MitMap } from "src/managers/MapFacade";
import { MapitUtils } from "src/managers/MapitUtils";
import { SheetAnalysisResult } from "src/common/managers/Types";
import { ColumnMapping } from "src/common/managers/Types";
import { processLayerData } from './FileLoaderXLSX';
import { ReferenceGeomComposite } from "src/managers/ReferenceGeomComposite";
import { ManagedWindow } from "src/WindowManager/WindowState";

export type ImportPrevSettingsEvent = {
   type: "submit" | "cancel" | "startOver",
   checked: boolean,
};

export function ImportPrevSettings(props: {
   managedWindow?: ManagedWindow,
   fileName?: string,
   sheetAnalysisResult?: SheetAnalysisResult,
   worksheet?: any,
   sourcePrompt?: string,
   savedColumnMapping?: ColumnMapping,
   savedLayerType?: string,
   savedImportTransformation?: any,
   onSubmit?: (evt: ImportPrevSettingsEvent) => void,
   onCancel?: (evt: ImportPrevSettingsEvent) => void,
   onStartOver?: (evt: ImportPrevSettingsEvent) => void,
}) {
   const [checked] = useState(false);
   const [previewLayerInfo, setPreviewLayerInfo] = useState<{
      sources: any,
      layers: any
   } | null>(null);
   const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
   const [isGeocoding, setIsGeocoding] = useState(false);
   const [geocodingProgress, setGeocodingProgress] = useState<string>("");
   const [layerHandle, setLayerHandle] = useState<MitLayerHandle | null>(null);
   const [isMapReady, setIsMapReady] = useState(false);
   const isGeocodingRef = useRef(false);
   const { state: _mapitState, dispatch: _mapitStateDispatch } = useContext(MapitStateContext)
   const [mapRef, setMapRef] = useState<MitMap | null>(null);

   useEffect(() => {
      const dataValues = extractPreviewData();
      if (!dataValues) {
         setPreviewLayerInfo(null);
         return;
      }
   }, [props.worksheet, props.savedColumnMapping, props.savedLayerType, props.fileName, props.sheetAnalysisResult]);

   const [processedLayerType, setProcessedLayerType] = useState<string | null>(null);

   // Reset processed layer type and layer handle when props change
   useEffect(() => {
      setProcessedLayerType(null);
      setLayerHandle(null);
      setPreviewLayerInfo(null);
      setIsMapReady(false);
   }, [props.savedLayerType, props.fileName, props.savedColumnMapping]);

   // Separate useEffect for map updates when layer handle changes
   useEffect(() => {
      if (!mapRef || !layerHandle) return;

      try {
         const sources: any = {};
         const layers: any = {};

         setPreviewLayerInfo({ sources, layers });
         if (mapRef && layerHandle) {
            mapRef.addLayer(layerHandle);
         }

         if (mapRef && layerHandle) {
            mapRef.zoomToFeature(layerHandle);
         }

      } catch (error) {
         console.error("Error updating map with layer handle:", error);
         setPreviewLayerInfo(null);
      }
   }, [layerHandle, mapRef]);

   // Create data layer when map is ready
   useEffect(() => {
      if (!mapRef) return;

      // Only process each layer type once
      const currentLayerType = props.savedLayerType || "default";
      if (processedLayerType === currentLayerType) {
         return;
      }

      // Prevent re-running geocoding if already in progress
      if (props.savedLayerType === LayerType.GeoCode && isGeocodingRef.current) {
         return;
      }

      setProcessedLayerType(currentLayerType);

      const checkForValidGeometry = (layerInfo: LayerInfo, dataValues: any): boolean => {
         if (layerInfo.type === LayerType.PointWGS84 || layerInfo.type === LayerType.GeoCode) {
            const latData = dataValues[MitDataType.Coord_WGS84_Lat];
            const lonData = dataValues[MitDataType.Coord_WGS84_Lon];

            if (!latData || !lonData || latData.length === 0 || lonData.length === 0) {
               return false;
            }

            for (let i = 0; i < latData.length; i++) {
               if (latData[i] !== -1 && lonData[i] !== -1 &&
                  latData[i] !== null && lonData[i] !== null &&
                  !isNaN(latData[i]) && !isNaN(lonData[i])) {
                  return true;
               }
            }
            return false;
         }

         if (layerInfo.type === LayerType.AreaCadaster || layerInfo.type === LayerType.AreaProperty || layerInfo.type === LayerType.AreaSFE) {
            const geoJsonData = dataValues[MitDataType.GeoJSON_Features];

            console.debug(`Checking geometry for ${layerInfo.type}:`, {
               hasGeoJsonData: !!geoJsonData,
               geoJsonLength: geoJsonData?.length,
               geoJsonSample: geoJsonData?.slice(0, 2)
            });

            if (!geoJsonData || geoJsonData.length === 0) {
               console.debug(`No GeoJSON data found for ${layerInfo.type}`);
               return false;
            }

            // Check if we have at least one valid GeoJSON feature
            for (let i = 0; i < geoJsonData.length; i++) {
               if (geoJsonData[i] && geoJsonData[i] !== "" && geoJsonData[i] !== null) {
                  try {
                     let parsed: any;
                     if (typeof geoJsonData[i] === 'object') {
                        parsed = geoJsonData[i];
                     } else {
                        parsed = JSON.parse(geoJsonData[i]);
                     }
                     // Check for different GeoJSON structures
                     let hasValidGeometry = false;

                     if (parsed && parsed.geometry && parsed.geometry.coordinates) {
                        hasValidGeometry = true;
                     } else if (parsed && parsed.features && Array.isArray(parsed.features) && parsed.features.length > 0) {
                        const firstFeature = parsed.features[0];
                        if (firstFeature && firstFeature.geometry && firstFeature.geometry.coordinates) {
                           hasValidGeometry = true;
                        }
                     }

                     if (hasValidGeometry) {
                        console.log(`Valid GeoJSON found at index ${i} for ${layerInfo.type}`);
                        return true;
                     }
                  } catch (e) {
                     console.log("Invalid GeoJSON in preview:", e);
                  }
               }
            }
            console.debug(`No valid GeoJSON features found for ${layerInfo.type}`);
            return false;
         }

         return true;
      };

      const createPreviewLayer = async () => {
         try {
            const dataValues = extractPreviewData();
            if (!dataValues) return;

            const savedStyling = getSavedStylingData();

            // Create base layerInfo
            let layerType = props.savedLayerType as LayerType || LayerType.GeoJSON_Point;
            const layerInfo: LayerInfo = {
               layerId: -1,
               datasetname: "Preview Layer",
               filename: props.fileName || "preview",
               type: layerType,
               readonly: false,
               columnMapping: props.savedColumnMapping || {},
               analysisResult: props.sheetAnalysisResult || {
                  noOfRows: 0,
                  noOfColumns: 0,
                  headers: [],
                  columnNames: [],
                  columns: []
               },
               styling: savedStyling?.styling || { color: SettingsManager.getSystemSetting("areaMapDefaultFillColor", "teal") } || {},
               crs: 4326,
               layerVariability: LayerVariabilityType.NormalLayer,
               data: dataValues,
               datasheet: props.worksheet,
               propertiesToDisplay: savedStyling?.propertiesToDisplay || [],
               visible: true,
               importTransformation: props.savedImportTransformation
            };

            // Handle geocoding layers
            if (props.savedLayerType === LayerType.GeoCode ||
               props.savedLayerType === LayerType.AreaCadaster ||
               props.savedLayerType === LayerType.AreaProperty ||
               props.savedLayerType === LayerType.AreaSFE) {
               isGeocodingRef.current = true;
               setIsGeocoding(true);
               setGeocodingProgress("Preparing for geocoding...");
            }

            // Use the shared processLayerData function
            processLayerData(
               layerInfo,
               dataValues,
               {
                  isPreview: true,
                  maxPreviewRows: 1,
                  onGeocodingStart: (_total, _type) => {
                     setGeocodingProgress(`Preview cannot be shown`);
                  },
                  onGeocodingProgress: (_completed, _total) => {
                     if (props.savedLayerType === LayerType.GeoCode) {
                        setGeocodingProgress(`Preview cannot be shown`);
                     } else if (props.savedLayerType === LayerType.AreaCadaster) {
                        setGeocodingProgress(`Preview cannot be shown`);
                     } else if (props.savedLayerType === LayerType.AreaSFE) {
                        setGeocodingProgress(`Preview cannot be shown`);
                     }
                  }
               }
            ).then(async ({ layerInfo: processedLayerInfo, dataValues: processedDataValues }) => {
               const finalLayerInfo = {
                  ...processedLayerInfo,
                  datasheet: props.worksheet,
                  data: processedDataValues,
                  propertiesToDisplay: savedStyling?.propertiesToDisplay || [],
                  visible: true
               };

               // Set geometry reference for area layers
               if (MapitUtils.isAreaLayerRequireingGeoData(finalLayerInfo.type)) {
                  await ReferenceGeomComposite.ensureRequiredGeomData(layerInfo.type);
                  finalLayerInfo.geometryReferenceFromMapit = { geoType: finalLayerInfo.type };
               }

               // Check if geocoding produced valid geometry
               const hasValidGeometry = checkForValidGeometry(finalLayerInfo, processedDataValues);

               if (!hasValidGeometry) {
                  setPreviewLayerInfo(null);
                  isGeocodingRef.current = false;
                  setIsGeocoding(true); // Keep showing message, but as error
                  setGeocodingProgress("Preview cannot be shown");
               } else {
                  const newLayerHandle = createLayerFromInfo(finalLayerInfo, mapRef);
                  if (newLayerHandle) {
                     setLayerHandle(newLayerHandle);
                  }
                  isGeocodingRef.current = false;
                  setIsGeocoding(false);
                  setGeocodingProgress("");
               }
            }).catch(error => {
               console.error("Error processing layer data:", error);
               setPreviewLayerInfo(null);
               isGeocodingRef.current = false;
               setIsGeocoding(true); // Show error message
               setGeocodingProgress("Preview cannot be shown");
            });
         } catch (error) {
            console.error("Error in createPreviewLayer:", error);
            setPreviewLayerInfo(null);
            isGeocodingRef.current = false;
            setIsGeocoding(true); // Show error message
            setGeocodingProgress("Preview cannot be shown");
         }
      };

      createPreviewLayer();
   }, [mapRef, processedLayerType, props.savedLayerType, props.fileName, props.savedColumnMapping]);

   // Helper function to create layer from layerInfo
   const createLayerFromInfo = (layerInfo: LayerInfo, mapRef: MitMap): MitLayerHandle | null => {
      try {
         const geoJson = MapitLayerFactory.getGeometryFromLayerInfo(layerInfo, true);
         if (!geoJson) return null;

         layerInfo.geoJson = geoJson;
         layerInfo.propertiesInGeoJson = Object.keys(layerInfo.data || {}).filter(key =>
            layerInfo.data && layerInfo.data[key] && Array.isArray(layerInfo.data[key]) && layerInfo.data[key].length > 0
         );

         // Convert to GeoJSON type to match FileLoader behavior (after geometry processing)
         layerInfo.type = LayerType.GeoJSON;

         const mapInfo = {
            popUpPictureSize: 1,
            popUpPictureLayout: 1
         } as any;

         const featureAvailability = {} as any;

         const { layerHandle } = MapitLayerFactory.createADataLayer(
            mapRef!,
            layerInfo,
            mapInfo,
            featureAvailability
         ) as { layerHandle: MitLayerHandle };

         return layerHandle;
      } catch (error) {
         console.error("Error creating layer from info:", error);
         return null;
      }
   };

   function onSubmitHandler() {
      if (props.onSubmit) {
         props.onSubmit({ type: "submit", checked: checked });
      }
   }

   function onCancelHandler() {
      if (props.onCancel) {
         props.onCancel({ type: "cancel", checked: checked });
      }
   }

   function onStartOverHandler() {
      if (props.onStartOver) {
         props.onStartOver({ type: "startOver", checked: checked });
      }
   }


   // Get appropriate icon and display name based on layer type
   const getLayerTypeInfo = (layerType: string) => {
      if (layerType.toLowerCase().includes('point')) {
         return {
            icon: <FaMapMarkerAlt style={{ color: "var(--viamapGreen)" }} />,
            displayName: "Point"
         };
      } else {
         return {
            icon: <MdImage style={{ color: "var(--viamapGreen)" }} />,
            displayName: "Polygon"
         };
      }
   };

   // Generate translated source prompt
   const getTranslatedSourcePrompt = () => {
      if (props.sourcePrompt === "filename" && props.fileName) {
         return Localization.getFormattedText("Reuse import settings previously used for '{fileName}'?", { fileName: props.fileName });
      } else if (props.sourcePrompt === "columns" && props.sheetAnalysisResult?.columnNames) {
         const columnNames = props.sheetAnalysisResult.columnNames;
         const displayColumns = columnNames.slice(0, 5);
         const remainingCount = columnNames.length - 5;

         return (
            <div style={{ fontSize: "16px", lineHeight: "1.5", maxWidth: "100%", overflow: "hidden" }}>
               <div>
                  {Localization.getText("Reuse import settings from similar columns?")}
               </div>
               <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "2px",
                  alignItems: "center",
                  marginLeft: "20px",
                  maxWidth: "100%",
                  overflow: "hidden"
               }}>
                  {displayColumns.map((col, index) => (
                     <span
                        key={index}
                        style={{
                           backgroundColor: "var(--viamapGreen)",
                           color: "white",
                           padding: "4px 8px",
                           borderRadius: "4px",
                           fontSize: "12px",
                           fontWeight: "500",
                           fontFamily: "monospace",
                           maxWidth: "200px",
                           overflow: "hidden",
                           textOverflow: "ellipsis",
                           whiteSpace: "nowrap",
                           flexShrink: 1
                        }}
                     >
                        {col}
                     </span>
                  ))}
                  {remainingCount > 0 && (
                     <span style={{
                        color: "#6c757d",
                        fontSize: "12px",
                        fontWeight: "500",
                        fontStyle: "italic"
                     }}>
                        +{remainingCount} {Localization.getText("more")}
                     </span>
                  )}
               </div>
            </div>
         );
      }
      return props.sourcePrompt || "";
   };

   // Get saved styling data
   const getSavedStylingData = () => {
      if (!props.fileName) return null;

      // Create a mock layerInfo to get saved styling
      const mockLayerInfo = {
         filename: props.fileName,
         analysisResult: props.sheetAnalysisResult
      };

      return RememberImportsManager.getRememberedStyling(mockLayerInfo as any);
   };


   const extractPreviewData = () => {
      if (!props.worksheet) {
         return null;
      }

      if (!props.savedColumnMapping) {
         return null;
      }

      let dataValues: any = {};
      const maxPreviewRows = 10;

      Object.keys(props.savedColumnMapping).forEach((dataType) => {
         const column = props.savedColumnMapping![dataType as MitDataType];
         const fullData = SheetFunc.getColumn(props.worksheet, column, dataType as MitDataType);

         if (props.savedLayerType === LayerType.GeoCode) {
            dataValues[dataType] = fullData.slice(0, maxPreviewRows);
         } else {
            dataValues[dataType] = fullData;
         }
      });

      return dataValues;
   };









   if (props.managedWindow?.state == "open") {
      // Don't render if we don't have the required data
      if (!props.savedColumnMapping) {
         return null;
      }

      return ReactDOM.createPortal(
         <AdvancedDragModal
            window={props.managedWindow}
            variant={"NSDark"}
            PosDefault={{ top: "30%", width: "600px", left: "50%", transform: "translate(-50%, -50%)" }}
            title={Localization.getText("Reuse Previous Import Settings")}
            onClose={onCancelHandler}
         >
            <ADModalInfo>
               {getTranslatedSourcePrompt()}
            </ADModalInfo>

            <ADModalBody>
               <GenerateSavedSettingsPreview
                  savedLayerType={props.savedLayerType as LayerType}
                  savedColumnMapping={props.savedColumnMapping as ColumnMapping}
                  savedStyling={getSavedStylingData()}
                  isPreviewExpanded={isPreviewExpanded}
                  setIsPreviewExpanded={setIsPreviewExpanded}
                  getLayerTypeInfo={getLayerTypeInfo}
                  savedImportTransformation={props.savedImportTransformation}
               />

               {/* Show geocoding progress for GeoCode and AreaCadaster layers */}
               {(props.savedLayerType === LayerType.GeoCode || props.savedLayerType === LayerType.AreaCadaster || props.savedLayerType === LayerType.AreaSFE) && isGeocoding ? (
                  <div style={{
                     backgroundColor: "#f8f9fa",
                     border: "1px solid #dee2e6",
                     borderRadius: "4px",
                     padding: "20px",
                     textAlign: "center",
                     marginBottom: "20px"
                  }}>
                     <MdMessage style={{
                        fontSize: "24px",
                        color: "#6c757d",
                        marginBottom: "10px"
                     }} />
                     <div style={{
                        color: "#6c757d",
                        fontSize: "14px"
                     }}>
                        {Localization.getText(geocodingProgress)}
                     </div>
                  </div>
               ) : (
                  <MapitPreview
                     savedLayerType={props.savedLayerType as LayerType}
                     previewLayerInfo={previewLayerInfo as { sources: any, layers: any }}
                     setRef={(map) => setMapRef(map as MitMap)}
                     isMapReady={isMapReady}
                  />
               )}
            </ADModalBody>

            <ADModalFooter>

               {/* 
               TODO: Re-add "Do not ask me again" checkbox functionality later, hvilken funktionalitet ønskes her?
               <GlassCheckbox
                  checked={checked}
                  onClick={() => setChecked(!checked)}
                  style={{ fontSize: "12px", marginRight: "auto", marginLeft: "6px" }}
               >
                  {Localization.getText("Do not ask me again")}
               </GlassCheckbox> */}


               <GlassButton onClick={() => onSubmitHandler()}>
                  {Localization.getText("Reuse Settings")}
               </GlassButton>
               <GlassButton onClick={() => onStartOverHandler()}>
                  {Localization.getText("Start Fresh")}
               </GlassButton>
            </ADModalFooter>
         </AdvancedDragModal>,
         document.getElementById("Mit-MapOverlay") || document.body
      );
   }
   return null;
}


function MapitPreview(props: {
   savedLayerType: LayerType,
   previewLayerInfo: {
      sources: any,
      layers: any
   }
   setRef: (ref: MitMap) => void
   isMapReady: boolean
}) {

   return (
      <div
         id="mapit-preview-2423"
         style={{
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            padding: "15px",
            marginTop: "15px",
         }}
      >
         <h6 style={{ marginBottom: "15px", color: "#495057" }}>
            {Localization.getText("Preview")}
            {(props.savedLayerType === LayerType.GeoCode || props.savedLayerType === LayerType.AreaCadaster || props.savedLayerType === LayerType.AreaSFE) && (
               <span style={{
                  fontStyle: "italic",
                  fontSize: "0.8em",
                  color: "#6c757d",
                  marginLeft: "8px"
               }}>
                  {Localization.getText("(not all rows are shown)")}
               </span>
            )}
         </h6>

         <div style={{ height: "400px", width: "100%" }}>
            <EmbeddedVectorMapEnhanced
               orthoPhoto={false}
               sources={props.previewLayerInfo?.sources}
               layers={props.previewLayerInfo?.layers}
               height="400px"
               width="100%"
               navigationControls={false}
               fullScreenControl={false}
               drawControls={false}
               onMapReady={(map) => {
                  props.setRef(map);

               }}
               options={{
                  attributionControl: false
               }}
            />
         </div>
      </div>
   )
}

// Generate preview of saved settings
function GenerateSavedSettingsPreview(props: {
   savedLayerType: LayerType,
   savedColumnMapping: ColumnMapping,
   savedStyling: any
   isPreviewExpanded: boolean
   savedImportTransformation: any
   setIsPreviewExpanded: (isPreviewExpanded: boolean) => void
   getLayerTypeInfo: (layerType: LayerType) => {
      icon: React.ReactNode,
      displayName: string
   }
}) {
   const savedStyling = props.savedStyling;

   return (
      <div className={`GlassFoldUdBox ${props.isPreviewExpanded ? "open" : ""}`} style={{ marginTop: "15px" }}>
         {/* Header with GlassFoldUdBox style */}
         <div
            className="Head"
            onClick={() => props.setIsPreviewExpanded(!props.isPreviewExpanded)}
            style={{
               cursor: "pointer",
               display: "flex",
               alignItems: "center",
               justifyContent: "space-between",
               padding: "12px 15px",
               backgroundColor: props.isPreviewExpanded ? "#f8f9fa" : "transparent",
               borderBottom: props.isPreviewExpanded ? "1px solid #ced4da" : "none",
               borderRadius: props.isPreviewExpanded ? "4px 4px 0 0" : "4px",
               transition: "all 0.2s ease"
            }}
         >
            <h5 style={{ margin: 0, color: "var(--viamapGreen)", display: "flex", alignItems: "center", gap: "8px" }}>
               <MdPalette style={{ color: "var(--viamapGreen)" }} />
               {Localization.getText("Saved Settings Preview")}
            </h5>
            <div className="FoldCaret" style={{
               transform: props.isPreviewExpanded ? "rotate(90deg)" : "rotate(0deg)",
               transition: "transform 0.2s ease",
               display: "flex",
               alignItems: "center",
               justifyContent: "center"
            }}>
               <PiCaretRightLight style={{ color: "var(--viamapGreen)" }} />
            </div>
         </div>

         {/* Body with GlassFoldUdBox style */}
         <div className="Body" style={{ padding: "15px" }}>
            {/* Layer Type and Column Mapping */}
            {(props.savedLayerType || (props.savedColumnMapping && Object.keys(props.savedColumnMapping).length > 0)) && (
               <div style={{
                  marginBottom: "20px"
               }}>
                  <h6 style={{ marginBottom: "8px", color: "#495057", display: "flex", alignItems: "center", gap: "8px" }}>
                     {props.savedLayerType ? props.getLayerTypeInfo(props.savedLayerType).icon : <MdMessage style={{ color: "var(--viamapGreen)" }} />}
                     {Localization.getText("Import Settings")}
                  </h6>
                  <div style={{
                     backgroundColor: "white",
                     padding: "8px",
                     borderRadius: "3px",
                     fontSize: "13px",
                     fontFamily: "monospace"
                  }}>
                     {props.savedLayerType && (
                        <div style={{ marginBottom: "4px" }}>
                           <span style={{ color: "var(--viamapGreen)", fontWeight: "bold" }}>
                              {Localization.getText("Type")}:
                           </span> {Localization.getText(props.getLayerTypeInfo(props.savedLayerType).displayName)}
                        </div>
                     )}
                     {props.savedColumnMapping && Object.keys(props.savedColumnMapping).length > 0 && (
                        <>
                           {props.savedLayerType && <div style={{ marginBottom: "4px", borderTop: "1px solid #eee", paddingTop: "4px" }}></div>}
                           {Object.entries(props.savedColumnMapping)
                              .filter(([dataType, _columnIndex]) => !Number.isInteger(Number(dataType)))
                              .map(([dataType, _columnIndex]) => (
                                 <div key={dataType} style={{ marginBottom: "2px" }}>
                                    <span style={{ color: "var(--viamapGreen)", fontWeight: "bold" }}>
                                       {Localization.getText(dataType)}:
                                    </span> {Localization.getText("Column") + " " + _columnIndex}
                                 </div>
                              ))}
                           {props.savedImportTransformation && Object.keys(props.savedImportTransformation).length > 0 && (
                              <>
                                 {props.savedLayerType && <div style={{ marginBottom: "4px", borderTop: "1px solid #eee", paddingTop: "4px" }}></div>}

                                 {Object.entries(props.savedImportTransformation).map(([key, value]) => (
                                    <div key={key} style={{ marginBottom: "2px" }}>
                                       <span style={{ color: "var(--viamapGreen)", fontWeight: "bold" }}>
                                          {key}
                                       </span> {String(value)}
                                    </div>
                                 ))}
                              </>
                           )}

                        </>
                     )}
                  </div>

               </div>
            )}



            {/* Styling */}
            {savedStyling?.styling && (
               <div style={{ marginBottom: "15px" }}>
                  <h6 style={{ marginBottom: "8px", color: "#495057", display: "flex", alignItems: "center", gap: "8px" }}>
                     <MdPalette style={{ color: "var(--viamapGreen)" }} />
                     {Localization.getText("Layer Styling")}
                  </h6>
                  <div style={{
                     backgroundColor: "white",
                     padding: "8px",
                     borderRadius: "3px",
                     fontSize: "13px",
                     fontFamily: "monospace"
                  }}>
                     {Object.entries(savedStyling.styling).slice(0, 4).map(([key, value]) => (
                        <div key={key} style={{ marginBottom: "2px" }}>
                           <span style={{ color: "var(--viamapGreen)", fontWeight: "bold" }}>
                              {key}:
                           </span> {typeof value === 'string' && value.startsWith('#') ? (
                              <span style={{
                                 backgroundColor: value,
                                 color: "white",
                                 padding: "1px 4px",
                                 borderRadius: "2px",
                                 fontSize: "11px"
                              }}>
                                 {value}
                              </span>
                           ) : String(value)}
                        </div>
                     ))}
                     {Object.keys(savedStyling.styling).length > 4 && (
                        <div style={{ color: "#6c757d", fontSize: "11px" }}>
                           +{Object.keys(savedStyling.styling).length - 4} {Localization.getText("more settings")}
                        </div>
                     )}
                  </div>
               </div>
            )}

            {/* Properties to Display */}
            {savedStyling?.propertiesToDisplay && savedStyling.propertiesToDisplay.length > 0 && (
               <div>
                  <h6 style={{ marginBottom: "8px", color: "#495057", display: "flex", alignItems: "center", gap: "8px" }}>
                     <MdMessage style={{ color: "var(--viamapGreen)" }} />
                     {Localization.getText("Popup Properties")}
                  </h6>
                  <div style={{
                     backgroundColor: "white",
                     padding: "8px",
                     borderRadius: "3px",
                     fontSize: "13px"
                  }}>
                     {savedStyling.propertiesToDisplay.slice(0, 3).map((prop, index) => (
                        <div key={index} style={{ marginBottom: "2px" }}>
                           <span style={{ color: "var(--viamapGreen)", fontWeight: "bold" }}>
                              {prop.name}
                           </span>
                           {prop.display?.aliasName && (
                              <span style={{ color: "#6c757d", fontSize: "11px" }}>
                                 {" "}({Localization.getText("alias")}: {prop.display.aliasName})
                              </span>
                           )}
                        </div>
                     ))}
                     {savedStyling.propertiesToDisplay.length > 3 && (
                        <div style={{ color: "#6c757d", fontSize: "11px" }}>
                           +{savedStyling.propertiesToDisplay.length - 3} {Localization.getText("more properties")}
                        </div>
                     )}
                  </div>
               </div>
            )}
         </div>
      </div>
   );
};